<template>
  <div class="projects-list-local">
    <!-- Loader overlay -->
    <v-overlay :value="!isLoaded">
      <v-progress-circular indeterminate size="64" />
    </v-overlay>

    <template v-if="isLoaded">
      <v-col cols="12">
        <v-text-field v-model="search" label="Recherche Nom ou Prénom" outlined @keyup.enter="goToUpSideDownWorld" />
      </v-col>

      <v-data-table :items="localProjects" :headers="headers" :items-per-page="itemsPerPage" :search="search"
        :sort-by="sortBy" :sort-desc="sortDesc" :footer-props="footerOptions">
        <template #item="{ item }">
          <tr @click="goToProjectPage(item)">
            <td class="text-start">
              {{ item.patient.fullname }} <br>
              <span>{{ item.center }}</span>
            </td>
            <td class="text-start">
              {{ item.product_name }}
            </td>
            <td class="text-start">
              {{ item.created_at | removeTime }}
            </td>
            <td class="text-start">
              <v-icon v-if="item.transferable && !item.idc_locked" class="ml-8" @click.stop="sendProject(item)">
                mdi-send
              </v-icon>
            </td>
          </tr>
        </template>
      </v-data-table>

      <app-bottom-nav @click-second="$router.push('projects-list-remote')" @click-first="goToNewProjectPage">
        <template #first>
          Nouvelle fiche
        </template>
        <template #second>
          Base Serveur
        </template>
      </app-bottom-nav>
    </template>
  </div>
</template>

<script>
import ErrorHandlingMixin from "@/mixins/ErrorHandlingMixin";
import { productsObject } from "@/utils";
const uuid = require("uuid/v4");
import { AppBottomNav } from "../../shared/components";
import { mapGetters } from "vuex";
import { httpClient } from "@/shared/services";


export default {
  name: "AppProjectsListLocal",

  components: {
    AppBottomNav
  },

  mixins: [ErrorHandlingMixin],

  data() {
    return {
      isLoaded: false,

      headers: [
        {
          text: "Patient",
          value: "patient.fullname"
        },
        {
          text: "Fiche",
          value: "product_name"
        },
        {
          text: "Date",
          value: "created_at"
        },
        { text: "", value: "actions", sortable: false }
      ],
      itemsPerPage: 500,
      search: "",
      sortBy: "created_at",
      sortDesc: false,
      footerOptions: {
        itemsPerPageOptions: [100, 500, 1000]
      },

      projects: [],
      patients: []
    };
  },

  computed: {
    ...mapGetters({
      getLoggedInUserId: "auth/getLoggedInUserId"
    }),

    localProjects() {
      return this.projects.map(project => {
        project.product_name = productsObject[project.product_model].name;
        return project;
      });
    }
  },

  async created() {
    await this.init();
  },

  methods: {
    // Initialize data.
    // Fetch patients and projects from local db
    async init() {
      try {
        // Set loading state
        this.isLoaded = false;

        this.projects = await this.$db.projects.orderBy("created_at").toArray();
        this.patients = await this.$db.patients.toArray();
      } catch (error) {


        alert(error);

      } finally {
        this.isLoaded = true;
      }
    },

    // Handle project submition
    async sendProject(project) {
      try {
        // Set loading state
        this.isLoaded = false;

        // Submit project object
        await this.submitProject(project);

        // Lunch submitMedias and submitProduct promises "parallel mode"
        // These way code will not broke if one of them fail
        // For example, if media request failed we want the product request to run no matter what
        const mediasPromises = await this.submitMedias(project);
        const mediasResult = await Promise.allSettled(mediasPromises).then(
          res => res
        );

        let mediasPassed = 0;
        mediasResult.forEach(res => {
          if (res.status === "fulfilled") {
            mediasPassed++;
          } else if (res.status === "rejected" && res.reason) {
            console.error("Res", res);
            console.error("Reason", res.reason);
            console.error("Error Name", res.reason.name);
            console.error("Error Message", res.reason.message);
            console.error("Error Stack", res.reason.stack);
            console.error("Status", res.status);
          }
        });

        if (mediasPassed !== mediasResult.length) {
          this.handleError(null, {
            title: "Médias",
            text: `${mediasPassed} / ${mediasResult.length} médias transférés.`,
            type: "warning"
          });
        }

        const productResult = await this.submitProduct(project)
          .then(() => true)
          .catch(err => {
            this.handleError(err, { title: "Probléme fiche" });

            return false;
          });

        // TODO: Needs refactoring
        // Prepare success message for user
        let message = "";
        if (productResult) {
          message += "La fiche produit a été tranférée. <br>";
        }

        if (mediasPassed === mediasResult.length) {
          message += "Tous les médias ont été tranférés. <br>";
        }

        if (message) {
          this.$notify({
            group: "main",
            type: "success",
            text: message,
            duration: 5000
          });
        }

        if (productResult && mediasPassed === mediasResult.length) {
          await this.deleteProject(project);
        }
      } catch (error) {
        console.log(error);

        // Dont throw error if is 401 response
        // Exception is handle in http-client.js
        if (error && error.response && error.response.status === 401) {
          return;
        }

        // Some known exception that could happen
        if (error.message.match(/PATIENT_NOT_FOUND/)) {
          this.handleError(
            error,
            "Le patient de cette fiche est introuvable. Nous ne pouvons pas procéder au transfert."
          );
          return false;
        } else if (error.message.match(/Network Error/)) {
          this.handleError(
            error,
            "La connexion avec la Base serveur a échouée. Veuillez réessayer plus tard"
          );
          return false;
        }

        throw error;
      } finally {
        this.isLoaded = true;
      }
    },

    // Submit project to server
    async submitProject(project) {
      let serverProject = false;

      try {
        serverProject = await httpClient.get(`/projects/${project.uuid}`);
      } catch (error) {
        if (error.response && error.response.status === 404) {
          console.log('Project not found. Creating new one.');
        } else {
          throw error;
        }
      }

      try {
        // If project exists on server, just update it
        if (serverProject) {
          return await this.$store.dispatch("projects/updateProject", {
            uuid: project.uuid,
            payload: project
          });
        }

        // Get patient from indexed DB by uuid
        const patient = await this.$db.patients.get(project.patient_uuid);

        // Patient will be undefined if dont exist, so handle it
        if (!patient) {
          throw new Error("PATIENT_NOT_FOUND");
        }
        // Create patient on server
        await this.$store.dispatch("patients/createPatient", patient);

        // Create project on server
        await this.$store.dispatch("projects/createProject", project);

        return true;
      } catch (error) {

        console.log(error);

        throw error;
      }
    },

    // Submit product to server
    async submitProduct(project) {
      try {
        // Get logged in user from indexed DB
        const user = await this.$db.user.toCollection().first();

        // local_version prop holds the product uuid
        // If is null, no product was created for this project locally
        if (project.local_version) {
          // Dynamically get indexeddb table name based on product_model prop
          const productTableName =
            productsObject[project.product_model].tableName;

          // Dynamically get vuex action based on product_model prop
          const createRemoteProductAction =
            productsObject[project.product_model].createRemoteProduct;

          // Get product based on local_version prop
          let localProduct = await this.$db[productTableName].get(
            project.local_version
          );

          // localProduct is undefined if no object was found
          // Only submit product if it exists
          if (localProduct) {
            // Adding user_id prop that holds the user id that created the product
            // TODO: get logged in user data from vuex
            localProduct.user_id = user.user.id;

            // Create remote product
            return await this.$store.dispatch(
              createRemoteProductAction,
              localProduct
            );
          } else if (!localProduct || localProduct == undefined) {
            // Go here if no product is found by local_version in IndexedDB
            // Try to get product by projects_uuid
            localProduct = await this.$db[productTableName].get({
              projects_uuid: project.uuid
            });
          }

          if (!localProduct || localProduct == undefined) {
            throw new Error("No product found in DB.");
          }

          return true;
        }
      } catch (error) {
        throw error;
      }
    },

    // Submit medias to server
    async submitMedias(project) {
      // Get medias from indexed DB by project_uuid
      const medias = await this.$db.medias
        .where({ project_uuid: project.uuid })
        .toArray();

      let promises = [];

      // Loop through medias
      for (let i = 0; i < medias.length; i++) {
        const media = medias[i];
        promises.push(this.submitMedia(media));
      }

      return promises;
    },

    async submitMedia(media) {
      try {
        // Validating data
        if (!media || media === undefined) throw new Error("Media is required");
        if (!media.project_uuid || media.project_uuid === undefined)
          throw new Error("This media don't have project");

        // Prepare form data payload
        let payload = new FormData();

        // if final_image starts with http, it's probably an url so keep it that way
        // otherwise we can consider that is base64 image, if so convert it to blob
        if (!media.final_image.startsWith("http")) {
          const final_image = this.dataURItoBlob(media.final_image);
          payload.append("image", final_image, "image.png");
        }

        // Append sketch to payload
        payload.append("croquis", media.croquis);

        // Verify if media already exists on server
        // sererMedia holds object if exists otherwise is false
        const serverMedia = await this.$store
          .dispatch("medias/fetchMedia", {
            project_uuid: media.project_uuid,
            media_uuid: media.uuid
          })
          .catch(() => false);

        // update media with new data if already exists on server
        if (serverMedia) {
          // needed 'cause PHP can't read formdata on PUT method
          // we do a POST request but make him believe it is PUT request
          // wtf right ?!
          payload.append("_method", "PUT");

          // Do the update
          await this.$store.dispatch("medias/updateSketchMedia", {
            project_uuid: media.project_uuid,
            media_uuid: media.uuid,
            payload
          });
        } else {
          // Fetch soft deleted media
          const mediaTrashedExist = await this.$store
            .dispatch("medias/fetchSoftDeletedMedia", {
              project_uuid: media.project_uuid,
              media_uuid: media.uuid
            })
            .catch(() => false);

          // Media is set as soft deleted in server, regenrate an uuid
          if (mediaTrashedExist) {
            const localMedia = await this.$db.medias.get(media.uuid);

            // Update uuid
            localMedia.uuid = uuid();

            // Add media with new uuid
            await this.$db.medias.add(localMedia);

            // Delete old media
            await this.$db.medias.delete(media.uuid);

            payload.append("croquis", localMedia.croquis);
            await this.$store.dispatch("medias/createSketchMedia", {
              project_uuid: media.project_uuid,
              media_uuid: localMedia.uuid,
              payload
            });
          } else {
            await this.$store.dispatch("medias/createSketchMedia", {
              project_uuid: media.project_uuid,
              media_uuid: media.uuid,
              payload
            });
          }
        }

        if (media.image && !media.image.startsWith("http")) {
          let data = new FormData();
          const blob = this.dataURItoBlob(media.image);
          data.append("image", blob, "image.png");

          await this.$store.dispatch("medias/createImageMedia", {
            project_uuid: media.project_uuid,
            media_uuid: media.uuid,
            payload: data
          });
        }

        return Promise.resolve(media);
      } catch (error) {
        throw error;
      }
    },

    // Delete project from local db
    async deleteProject(project) {
      try {
        const productTableName =
          productsObject[project.product_model].tableName;

        // Delete project
        await this.$db.projects.delete(project.uuid);

        // Delete patient
        await this.$db.patients.delete(project.patient.uuid);

        // Delete product
        await this.$db[productTableName].delete(project.product_uuid);

        // Get project medias from local db
        const localMedias = await this.$db.medias
          .where({ project_uuid: project.uuid })
          .toArray();

        // Create an array of medias keys
        let mediasKeysToDelete = [];
        for (let i = 0; i < localMedias.length; i++) {
          mediasKeysToDelete.push(localMedias[i].uuid);
        }

        // Delete medias
        await this.$db.medias.bulkDelete(mediasKeysToDelete);

        // Delete project from local projects array vy uuid prop
        for (var i = this.projects.length - 1; i >= 0; --i) {
          if (this.projects[i].uuid == project.uuid) {
            this.projects.splice(i, 1);
          }
        }
      } catch (error) {
        throw error;
      }
    },

    async updateProject(project) {
      try {
        // Update remote project
        await this.$store.dispatch("projects/updateProject", {
          uuid: project.uuid,
          payload: project
        });

        // Update product
        const productTableName =
          productsObject[project.product_model].tableName;
        const createRemoteProductAction =
          productsObject[project.product_model].createRemoteProduct;
        let localProduct = await this.$db[productTableName].get(
          project.local_version
        );

        // Add user_id prop
        const user = await this.$db.user.toCollection().first();
        localProduct.user_id = user.user.id;

        // Create remote product
        await this.$store.dispatch(createRemoteProductAction, localProduct);
      } catch (error) {
        const errorCode = error.response.data.error;

        if (
          errorCode ==
          "ERR_APPLICATOR_WORKFLOW_CANNOT_EDIT_PROJECT_MOLDING_ARRIVAL_REACHED"
        ) {
          this.$swal({
            icon: "error",
            title: "Erreur !",
            text: error.response.data.message
          });
        }

        return Promise.reject(error);
      }
    },

    async createProject(project) {
      try {
        // Get patient
        const localPatient = await this.$db.patients.get(project.patient_uuid);

        if (!localPatient) {
          throw new Error("FRONT_PATIENT_NOT_FOUND");
        }

        // Create remote patient
        await this.$store.dispatch("patients/createPatient", localPatient);

        // Create remote project
        await this.$store.dispatch("projects/createProject", project);

        const productTableName =
          productsObject[project.product_model].tableName;
        const createRemoteProductAction =
          productsObject[project.product_model].createRemoteProduct;

        let localProduct = await this.$db[productTableName].get(
          project.local_version
        );

        // Add user_id prop
        const user = await this.$db.user.toCollection().first();
        localProduct.user_id = user.user.id;

        // Create remote product
        await this.$store.dispatch(createRemoteProductAction, localProduct);
      } catch (err) {
        let error = err;

        const objectConstructor = {}.constructor;
        if (error.constructor === objectConstructor) {
          error = JSON.stringify(error);
        }

        const notification = {
          message: "Une erreur est survenue. Détail: " + error,
          type: "error"
        };
        this.$store.dispatch("notifications/add", notification);

        return Promise.reject(error);
      }
    },

    // Convert base64 image to Blob for sending to server
    dataURItoBlob(dataURI) {
      // convert base64 to raw binary data held in a string
      // doesn't handle URLEncoded DataURIs - see SO answer #6850276 for code that does this
      var byteString = atob(dataURI.split(",")[1]);

      // separate out the mime component
      var mimeString = dataURI
        .split(",")[0]
        .split(":")[1]
        .split(";")[0];

      // write the bytes of the string to an ArrayBuffer
      var ab = new ArrayBuffer(byteString.length);

      // create a view into the buffer
      var ia = new Uint8Array(ab);

      // set the bytes of the buffer to the correct values
      for (var i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }

      // write the ArrayBuffer to a blob, and you're done
      var blob = new Blob([ab], { type: mimeString });
      return blob;
    },

    // Go to New Project Page
    goToProjectPage(ev) {
      this.$router.push({
        name: "ProjectPage",
        params: { projectUuid: ev.uuid }
      });
    },

    goToNewProjectPage() {
      this.$router.push({ name: "CreateProject" });
    },

    // If debug code is given, go to debug view
    goToUpSideDownWorld() {
      const debugCode = process.env.VUE_APP_DEBUG_VIEW_CODE;

      if (this.search === debugCode) {
        this.$router.push({ name: "app-debug-view" });
      }
    }
  }
};
</script>

<style lang="scss">
.projects-list-local {
  display: flex;
  flex-direction: column;
  padding-bottom: 66px;

  .v-data-table {
    flex-grow: 1;

    table {
      border: none !important;

      * {
        border: none !important;
      }

      tbody {
        td {
          font-size: 14px !important;
          padding: 20px 16px !important;

          &:nth-last-child(5) {
            font-weight: bold;
            font-size: 13px !important;
            color: #006064;
            text-transform: uppercase;
          }

          &:nth-last-child(4) {
            font-weight: bold;
            font-size: 13px !important;
            text-transform: uppercase;
            color: #880e4f;
          }

          &:nth-last-child(3) {
            color: #880e4f;
            font-weight: 600;
          }

          &:nth-last-child(2) {
            i {
              font-size: 20px !important;
            }
          }

          span {
            font-weight: 400;
            font-size: 14px;
          }
        }
      }
    }

    .v-data-footer {
      border-top: none !important;
    }
  }
}
</style>
